import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setCurrentChannel, clearMentionAlertsForChannel, markChannelAsRead } from '../../store/slices/chatSlice';
import { dismissHighlightedToastsByKeys } from '../../store/slices/userNotificationSlice';
import HighlightNotificationToggle from './HighlightNotificationToggle';
import EnableAudioCaptionToggle from './EnableAudioCaptionToggle';
import WorkModeSpeechControls from './WorkModeSpeechControls';
import PresignedAvatar from './PresignedAvatar';
import { resolveUsersByIds } from '../../realm/offlineLookups';
import { findUserForDm, mergePeopleForLookup, normalizeMemberIds } from '../../utils/directMessageUtils';
import {
  getLastUnreadMentionInChannel,
} from '../../utils/mentionUtils';
import {
  formatChatNotification,
  formatPanelAccessNotification,
  formatTaskDelegatedNotification,
  formatUserNotification,
  getLastUnreadMessageInChannel,
} from '../../utils/notificationMessageUtils';
import {
  markPanelEventsSeen,
  dismissFabPanelNotification,
  clearAllFabPanelNotifications,
} from '../../store/slices/panelAccessSlice';
import {
  markTaskDelegationsSeen,
  dismissFabTaskNotification,
  clearAllFabTaskNotifications,
} from '../../store/slices/taskSlice';
import {
  markUserNotificationsSeen,
  dismissFabUserNotification,
  clearAllFabUserNotifications,
} from '../../store/slices/userNotificationSlice';

import {
  markMeetingEventsSeen,
  dismissFabMeetingNotification,
  clearAllFabMeetingNotifications,
} from '../../store/slices/meetingNotificationSlice';
import { buildChatNavigationSearch } from '../../utils/threadUtils';
import CompactRichEditor from './CompactRichEditor';
import { createNote, fetchMyNotes, updateNote } from '../../features/notes/services/noteService';
import { addNotification } from '../../store/slices/uiSlice';

const stripNoteHtml = (html) =>
  String(html || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const BELL_NOTE_TITLE = /^from notes #(\d+)$/i;

const nextBellNoteTitle = async () => {
  let next = 1;
  try {
    const notes = await fetchMyNotes();
    (Array.isArray(notes) ? notes : []).forEach((note) => {
      const match = String(note?.title || '').trim().match(BELL_NOTE_TITLE);
      if (!match) return;
      const number = Number.parseInt(match[1], 10);
      if (Number.isFinite(number) && number >= next) next = number + 1;
    });
  } catch {
    // Keep #1 if the list cannot be loaded.
  }
  return `from notes #${next}`;
};

const TakeNoteGlyph = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const ChannelRowAvatar = ({ channel }) => {
  const name = channel?.name || 'Channel';
  const initials = (name.substring(0, 2) || 'CH').toUpperCase();

  return (
    <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden mr-3 bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
      {channel?.imageUrl ? (
        <PresignedAvatar
          fileKey={channel.imageUrl}
          name={name}
          initials={initials}
          className="w-full h-full rounded-lg object-cover"
          fallbackClassName="w-full h-full rounded-lg bg-primary-500 flex items-center justify-center text-white text-sm font-bold"
        />
      ) : (
        <span className="text-primary-600 dark:text-primary-400 text-base font-bold leading-none" aria-hidden>
          #
        </span>
      )}
    </div>
  );
};

const DmRowAvatar = ({ otherUser }) => {
  const displayName =
    otherUser?.username ||
    [otherUser?.firstName, otherUser?.lastName].filter(Boolean).join(' ').trim() ||
    'User';
  const initials = (displayName[0] || 'U').toUpperCase();

  return (
    <div className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden mr-3 bg-gradient-to-br from-primary-400 to-primary-600 dark:from-primary-500 dark:to-primary-700 flex items-center justify-center">
      <PresignedAvatar
        fileKey={otherUser?.profilePicture}
        name={displayName}
        initials={initials}
        className="w-full h-full rounded-full object-cover"
        fallbackClassName="w-full h-full rounded-full flex items-center justify-center text-white font-semibold text-sm bg-gradient-to-br from-primary-400 to-primary-600 dark:from-primary-500 dark:to-primary-700"
      />
    </div>
  );
};

const DraggableNotificationBubble = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { unreadCounts, messages, lastViewed, mentionAlerts = [] } = useSelector((state) => state.chat);
  const { channels, directMessages } = useSelector((state) => state.channels);
  const { users } = useSelector((state) => state.users);
  const {
    fabPanelNotifications,
    unseenPanelEventCount,
  } = useSelector((state) => state.panelAccess);
  const {
    fabTaskNotifications,
    unseenTaskDelegationCount,
  } = useSelector((state) => state.tasks);
  const {
    fabMeetingNotifications = [],
    unseenMeetingCount = 0,
  } = useSelector((state) => state.meetingNotifications || {});
  const {
    fabUserNotifications = [],
    unseenUserNotificationCount = 0,
  } = useSelector((state) => state.userNotifications || {});

  const [position, setPosition] = useState({ x: window.innerWidth - 80, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const bubbleRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [dmUserDetails, setDmUserDetails] = useState([]);
  const [noteComposerOpen, setNoteComposerOpen] = useState(false);
  const [noteComposerKey, setNoteComposerKey] = useState(0);
  const [noteDraftHtml, setNoteDraftHtml] = useState('');
  const noteQuillRef = useRef(null);
  const noteHtmlRef = useRef('');
  const noteIdRef = useRef(null);
  const noteSaveTimerRef = useRef(null);
  const noteCreateLockRef = useRef(null);
  const isNavigatingRef = useRef(false);
  const touchHandledRef = useRef(false);

  // Bubble size constants
  const BUBBLE_SIZE = 50; // 50px width and height

  // Helper function to constrain position within window bounds
  const constrainPosition = (pos) => {
    const maxX = window.innerWidth - BUBBLE_SIZE;
    const maxY = window.innerHeight - BUBBLE_SIZE;
    return {
      x: Math.max(0, Math.min(pos.x || 0, maxX)),
      y: Math.max(0, Math.min(pos.y || 0, maxY))
    };
  };

  // Load saved position from localStorage on mount
  useEffect(() => {
    if (user) {
      const savedPosition = localStorage.getItem('notificationBubblePosition');
      if (savedPosition) {
        try {
          const parsed = JSON.parse(savedPosition);
          // Validate and constrain saved position to current window size
          const constrainedPosition = constrainPosition(parsed);
          setPosition(constrainedPosition);
        } catch (e) {
          console.error('Failed to parse saved position', e);
          // Reset to default position if parsing fails
          setPosition({ x: window.innerWidth - BUBBLE_SIZE - 20, y: 80 });
        }
      } else {
        // Default position if no saved position
        setPosition({ x: window.innerWidth - BUBBLE_SIZE - 20, y: 80 });
      }
    }
  }, [user]);

  // Save position to localStorage whenever it changes (only if user is logged in)
  useEffect(() => {
    if (user) {
      localStorage.setItem('notificationBubblePosition', JSON.stringify(position));
    }
  }, [position, user]);

  // Handle window resize - keep bubble within bounds
  useEffect(() => {
    const handleResize = () => {
      setPosition(prevPos => constrainPosition(prevPos));
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch user details for direct messages
  useEffect(() => {
    const fetchDMUserDetails = async () => {
      if (directMessages.length === 0) {
        setDmUserDetails([]);
        return;
      }

      try {
        const allMemberIds = [...new Set(
          directMessages.flatMap((dm) => normalizeMemberIds(dm.memberIds))
        )].filter((id) => id !== String(user?.id || ''));

        if (allMemberIds.length === 0) {
          setDmUserDetails([]);
          return;
        }

        const uniqueMemberIds = [...new Set(allMemberIds)];
        const resolved = await resolveUsersByIds(uniqueMemberIds, users);
        setDmUserDetails(resolved);
      } catch (error) {
        console.error('Error fetching DM user details:', error);
      }
    };

    fetchDMUserDetails();
  }, [directMessages, user?.id, users]);

  const dragStartedRef = useRef(false);

  // Calculate total unread message count - ensure we only count each channel once
  // Also ensure we're not double-counting by using a Set of channel IDs
  const processedChannelIds = new Set();
  const totalUnreadCount = Object.entries(unreadCounts || {}).reduce((sum, [channelId, count]) => {
    // Skip if we've already processed this channel ID
    if (processedChannelIds.has(channelId)) {
      return sum;
    }
    processedChannelIds.add(channelId);

    // Only count if it's a valid number and greater than 0
    const numCount = typeof count === 'number' ? count : parseInt(count) || 0;
    return sum + Math.max(0, numCount);
  }, 0);

  // Get channels and DMs with unread messages - ensure unique channel IDs
  const channelIdsWithUnread = new Set(
    Object.keys(unreadCounts || {}).filter(id => (unreadCounts[id] || 0) > 0)
  );
  const channelsWithUnread = (channels || []).filter(channel =>
    channel?.id && channelIdsWithUnread.has(channel.id)
  );
  const dmsWithUnread = (directMessages || []).filter(dm =>
    dm?.id && channelIdsWithUnread.has(dm.id)
  );

  const panelBadgeCount = Math.max(fabPanelNotifications.length, unseenPanelEventCount);
  const taskBadgeCount = Math.max(fabTaskNotifications.length, unseenTaskDelegationCount);
  const meetingBadgeCount = Math.max(fabMeetingNotifications.length, unseenMeetingCount);
  const userNotificationBadgeCount = Math.max(fabUserNotifications.length, unseenUserNotificationCount);
  const mentionAlertChannelIds = new Set(
    mentionAlerts.map((alert) => alert.channelId).filter(Boolean)
  );
  const totalBadgeCount = totalUnreadCount + panelBadgeCount + taskBadgeCount + meetingBadgeCount + userNotificationBadgeCount;
  const hasMentionAlerts = mentionAlerts.length > 0;
  const hasPanelNotifications = fabPanelNotifications.length > 0;
  const hasTaskNotifications = fabTaskNotifications.length > 0;
  const hasMeetingNotifications = fabMeetingNotifications.length > 0;
  const hasUserNotifications = fabUserNotifications.length > 0;

  const handleOpenDailyTasks = () => {
    dispatch(markPanelEventsSeen());
    window.dispatchEvent(new CustomEvent('workloop:open-daily-tasks'));
    navigate('/chat');
    setIsExpanded(false);
  };

  const handleOpenDelegatedTasks = () => {
    dispatch(markTaskDelegationsSeen());
    window.dispatchEvent(new CustomEvent('workloop:open-daily-tasks', {
      detail: { tab: 'delegated' },
    }));
    navigate('/chat');
    setIsExpanded(false);
  };

  const handleOpenTaskNotification = (event) => {
    const taskId = event?.task?.id;
    dispatch(markTaskDelegationsSeen());
    navigate('/chat');
    setIsExpanded(false);
    if (!taskId) {
      handleOpenDelegatedTasks();
      return;
    }
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('workloop:open-notification', {
        detail: { eventType: 'TASK_ASSIGNED', taskId },
      }));
    }, 80);
  };

  const handleDismissPanelNotification = (e, fabKey) => {
    e.stopPropagation();
    dispatch(dismissFabPanelNotification(fabKey));
  };

  const handleClearAllPanelNotifications = (e) => {
    e.stopPropagation();
    dispatch(clearAllFabPanelNotifications());
  };

  const handleDismissTaskNotification = (e, fabKey) => {
    e.stopPropagation();
    dispatch(dismissFabTaskNotification(fabKey));
  };

  const handleClearAllTaskNotifications = (e) => {
    e.stopPropagation();
    dispatch(clearAllFabTaskNotifications());
  };

  const handleOpenUserNotification = (event) => {
    dispatch(markUserNotificationsSeen());
    setIsExpanded(false);
    const eventType = String(event?.eventType || '').toUpperCase();
    const messageId = event?.sourceMessageId || event?.messageId || event?.chatMessageId;
    const channelId = event?.sourceChannelId || event?.channelId;
    const openInChat = eventType === 'MESSAGE_REACTION'
      || eventType === 'MENTION'
      || eventType === 'MESSAGE'
      || (Boolean(messageId) && !eventType.startsWith('TASK_') && !eventType.startsWith('WORKFLOW_'));

    navigate('/chat');
    window.setTimeout(() => {
      if (openInChat && messageId) {
        window.dispatchEvent(new CustomEvent('workloop:go-to-message', {
          detail: {
            messageId,
            channelId,
          },
        }));
        return;
      }
      window.dispatchEvent(new CustomEvent('workloop:open-notification', {
        detail: {
          eventType: event?.eventType,
          sourceChannelId: channelId,
          sourceMessageId: messageId,
          taskId: event?.taskId,
          notificationChannelId: event?.notificationChannelId,
        },
      }));
    }, 120);
  };

  const handleDismissUserNotification = (e, fabKey) => {
    e.stopPropagation();
    dispatch(dismissFabUserNotification(fabKey));
  };

  const handleClearAllUserNotifications = (e) => {
    e.stopPropagation();
    dispatch(clearAllFabUserNotifications());
  };

  const meetingTypeLabel = (type) => {
    switch (String(type || '').toUpperCase()) {
      case 'MEETING_REMINDER':
        return 'Starts in 15 min';
      case 'MEETING_UPDATED':
        return 'Updated';
      case 'MEETING_CANCELLED':
        return 'Cancelled';
      case 'MEETING_SCHEDULED':
      default:
        return 'Scheduled';
    }
  };

  const handleMeetingNotificationClick = (event) => {
    dispatch(markMeetingEventsSeen());
    dispatch(dismissFabMeetingNotification(event.fabKey));
    setIsExpanded(false);

    if (event.meetingLink && String(event.type).toUpperCase() === 'MEETING_REMINDER') {
      window.open(event.meetingLink, '_blank', 'noopener,noreferrer');
    }

    if (!event.channelId) {
      window.dispatchEvent(new CustomEvent('workloop:open-calendar'));
      navigate('/chat');
      return;
    }

    const allChannels = [...(channels || []), ...(directMessages || [])];
    const channel = allChannels.find((ch) => ch.id === event.channelId);
    if (channel) {
      dispatch(setCurrentChannel(channel));
      if (event.chatMessageId) {
        window.dispatchEvent(new CustomEvent('workloop:go-to-message', {
          detail: {
            messageId: event.chatMessageId,
            channelId: event.channelId,
          },
        }));
      } else {
        navigate('/chat');
      }
    } else {
      window.dispatchEvent(new CustomEvent('workloop:open-calendar'));
      navigate('/chat');
    }
  };

  const handleDismissMeetingNotification = (e, fabKey) => {
    e.stopPropagation();
    dispatch(dismissFabMeetingNotification(fabKey));
  };

  const handleClearAllMeetingNotifications = (e) => {
    e.stopPropagation();
    dispatch(clearAllFabMeetingNotifications());
  };

  useEffect(() => {
    if (isExpanded && hasPanelNotifications) {
      dispatch(markPanelEventsSeen());
    }
    if (isExpanded && hasMeetingNotifications) {
      dispatch(markMeetingEventsSeen());
    }
    if (isExpanded && hasUserNotifications) {
      dispatch(markUserNotificationsSeen());
    }
  }, [isExpanded, hasPanelNotifications, hasMeetingNotifications, hasUserNotifications, dispatch]);

  const hasUnreadMentionInChannel = (channelId) =>
    !!getLastUnreadMentionInChannel(
      messages[channelId] || [],
      user?.id,
      user?.username,
      lastViewed[channelId]
    );

  const channelsWithUnreadMentions = channelsWithUnread.filter((c) =>
    hasUnreadMentionInChannel(c.id)
  );
  const dmsWithUnreadMentions = dmsWithUnread.filter((dm) =>
    hasUnreadMentionInChannel(dm.id)
  );

  const mentionChannelIds = new Set([
    ...channelsWithUnreadMentions.map((c) => c.id),
    ...dmsWithUnreadMentions.map((d) => d.id),
    ...mentionAlertChannelIds,
  ]);

  const getLastActivityTimestamp = (channel) => {
    if (!channel?.id) return 0;
    let latest = 0;
    const consider = (value) => {
      if (value == null || value === '') return;
      const parsed = typeof value === 'number' ? value : new Date(value).getTime();
      if (!Number.isNaN(parsed) && parsed > latest) latest = parsed;
    };

    consider(channel.lastMessageAt);
    consider(channel.lastMessage?.createdAt);
    consider(channel.lastMessage?.timestamp);
    consider(channel.lastMessage?.updatedAt);
    consider(channel.updatedAt);
    consider(channel.createdAt);

    const channelMessages = messages[channel.id];
    if (Array.isArray(channelMessages) && channelMessages.length) {
      const last = channelMessages[channelMessages.length - 1];
      consider(last?.createdAt || last?.timestamp || last?.updatedAt);
      channelMessages.forEach((msg) => {
        consider(msg?.createdAt || msg?.timestamp || msg?.updatedAt);
      });
    }

    const unreadMsg = getLastUnreadMessageInChannel(
      channelMessages || [],
      user?.id,
      lastViewed[channel.id]
    );
    consider(unreadMsg?.createdAt || unreadMsg?.timestamp || unreadMsg?.updatedAt);

    const mentionMsg = getLastUnreadMentionInChannel(
      channelMessages || [],
      user?.id,
      user?.username,
      lastViewed[channel.id]
    );
    consider(mentionMsg?.createdAt || mentionMsg?.timestamp || mentionMsg?.updatedAt);

    const alert = mentionAlerts.find((item) => item.channelId === channel.id);
    consider(alert?.receivedAt);
    consider(alert?.message?.createdAt || alert?.message?.timestamp);
    return latest;
  };

  const byLatestActivity = (list) => [...(list || [])].sort((a, b) => {
    const timestampA = getLastActivityTimestamp(a);
    const timestampB = getLastActivityTimestamp(b);
    if (timestampA !== timestampB) return timestampB - timestampA;
    return String(a.name || a.id || '').localeCompare(String(b.name || b.id || ''));
  });

  const channelsWithUnreadMentionsAll = byLatestActivity((channels || []).filter(
    (c) => c?.id && mentionChannelIds.has(c.id)
  ));
  const dmsWithUnreadMentionsAll = byLatestActivity((directMessages || []).filter(
    (dm) => dm?.id && mentionChannelIds.has(dm.id)
  ));

  const channelsWithUnreadOnly = byLatestActivity(channelsWithUnread.filter(
    (c) => !mentionChannelIds.has(c.id)
  ));
  const dmsWithUnreadOnly = byLatestActivity(dmsWithUnread.filter(
    (dm) => !mentionChannelIds.has(dm.id)
  ));

  const mentionConversationCount =
    channelsWithUnreadMentionsAll.length + dmsWithUnreadMentionsAll.length;

  // Helper function to get other user from DM
  const getOtherUserFromDM = (dm) =>
    findUserForDm(dm, user?.id, mergePeopleForLookup(dmUserDetails, users));

  const getDmDisplayName = (otherUser) =>
    otherUser?.username?.trim() ||
    [otherUser?.firstName, otherUser?.lastName].filter(Boolean).join(' ').trim() ||
    'User';

  const withDmSenderName = (message, otherUser) => {
    if (!message || !otherUser) return message;
    return { ...message, senderName: getDmDisplayName(otherUser) };
  };


  const getMentionPreviewForChannel = (channelId, channelName, isDirectMessage, otherUser = null) => {
    const alert = mentionAlerts.find((item) => item.channelId === channelId);
    if (alert?.message) {
      return formatChatNotification({
        message: isDirectMessage ? withDmSenderName(alert.message, otherUser) : alert.message,
        channelName: alert.channelName || channelName,
        isDirectMessage,
        isMentioned: true,
        maxBodyLength: isDirectMessage ? 60 : 50,
      });
    }

    const mentionMsg = getLastUnreadMentionInChannel(
      messages[channelId] || [],
      user?.id,
      user?.username,
      lastViewed[channelId]
    );
    if (!mentionMsg) return null;

    return formatChatNotification({
      message: isDirectMessage ? withDmSenderName(mentionMsg, otherUser) : mentionMsg,
      channelName,
      isDirectMessage,
      isMentioned: true,
      maxBodyLength: isDirectMessage ? 60 : 50,
    });
  };

  const getUnreadRowPreview = (channelId, channelName, isDirectMessage, otherUser = null) => {
    const lastUnread = getLastUnreadMessageInChannel(
      messages[channelId] || [],
      user?.id,
      lastViewed[channelId]
    );
    if (!lastUnread) return null;

    return formatChatNotification({
      message: isDirectMessage ? withDmSenderName(lastUnread, otherUser) : lastUnread,
      channelName,
      isDirectMessage,
      isMentioned: false,
      maxBodyLength: isDirectMessage ? 60 : 50,
    });
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    if (noteComposerOpen) return;
    dragStartedRef.current = false;

    const rect = bubbleRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });

    setIsDragging(true);
  };



  const handleMouseMove = (e) => {
    if (!isDragging) return;
    dragStartedRef.current = true;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    setPosition(constrainPosition({ x: newX, y: newY }));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Handle touch events for mobile
  const handleTouchStart = (e) => {
    if (noteComposerOpen) return;
    const touch = e.touches[0];
    const rect = bubbleRef.current.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
    setIsDragging(true);
    setIsExpanded(false);
    e.preventDefault();
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const newX = touch.clientX - dragOffset.x;
    const newY = touch.clientY - dragOffset.y;

    setPosition(constrainPosition({ x: newX, y: newY }));
    e.preventDefault();
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Handle dragging from header - moves the bubble (and panel with it)
  const handleHeaderMouseDown = (e) => {
    // Don't start drag if clicking the close button or its children
    if (e.target.closest('button')) {
      return;
    }

    if (e.button !== 0) return;
    dragStartedRef.current = false;

    const rect = bubbleRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });

    setIsDragging(true);
    // Stop propagation to prevent panel's handlers from interfering
    e.stopPropagation();
  };

  // Handle touch events for header dragging
  const handleHeaderTouchStart = (e) => {
    // Don't start drag if touching the close button or its children
    if (e.target.closest('button')) {
      return;
    }

    const touch = e.touches[0];
    const rect = bubbleRef.current.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
    setIsDragging(true);
    // Stop propagation to prevent panel's handlers from interfering
    // Don't close panel when dragging from header (unlike bubble drag)
    e.stopPropagation();
    e.preventDefault();
  };

  const getTargetMessageForNavigation = (channelId, preferMention = false) => {
    const channelMessages = messages[channelId] || [];
    if (preferMention) {
      const mentionMsg = getLastUnreadMentionInChannel(
        channelMessages,
        user?.id,
        user?.username,
        lastViewed[channelId]
      );
      if (mentionMsg) return mentionMsg;
    }
    return getLastUnreadMessageInChannel(
      channelMessages,
      user?.id,
      lastViewed[channelId]
    );
  };

  // Handle channel/DM click - same pattern as ChatApp.js handleChannelSelect
  const handleChannelClick = (channel, e, isTouch = false, { preferMention = false } = {}) => {
    // If this is a click event and we already handled a touch, skip it
    if (!isTouch && touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }

    // Stop propagation to prevent panel from closing
    e?.stopPropagation();
    e?.preventDefault();

    // Ensure we have a valid channel object
    if (!channel || !channel.id) {
      console.error('❌ NotificationBubble: Invalid channel object:', channel);
      return;
    }

    console.log('🔔 NotificationBubble: Selecting channel', {
      channelId: channel.id,
      channelName: channel.name || 'DM',
      channelType: channel.type,
      hasId: !!channel.id,
      hasName: !!channel.name,
      hasMemberIds: !!channel.memberIds
    });

    // Mark that we're navigating to prevent click outside from firing
    isNavigatingRef.current = true;

    // Mark touch as handled if this is a touch event
    if (isTouch) {
      touchHandledRef.current = true;
    }

    // Dispatch setCurrentChannel (same as ChatApp's handleChannelSelect)
    dispatch(setCurrentChannel(channel));
    dispatch(clearMentionAlertsForChannel(channel.id));
    dispatch(markChannelAsRead(channel.id));
    dispatch(dismissHighlightedToastsByKeys([channel.id]));

    const targetMessage = getTargetMessageForNavigation(channel.id, preferMention);
    const search = targetMessage
      ? buildChatNavigationSearch(targetMessage, messages[channel.id] || [])
      : '';
    const chatPath = search ? `/chat?${search}` : '/chat';

    // Navigate to chat with thread/message deep-link when applicable
    navigate(chatPath);

    // Close the panel after a delay to ensure the click is processed
    // Use longer delay on mobile to allow navigation to complete
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    setTimeout(() => {
      setIsExpanded(false);
      // Reset navigation flag after a bit more time
      setTimeout(() => {
        isNavigatingRef.current = false;
        touchHandledRef.current = false;
      }, 100);
    }, isMobile ? 300 : 100);
  };

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if we're in the middle of navigating
      if (isNavigatingRef.current) {
        return;
      }

      if (isExpanded && bubbleRef.current && !bubbleRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    if (!isExpanded) {
      flushBellNoteSave();
    }
    if (isExpanded) {
      // Use a slight delay to allow click events inside the panel to fire first
      // Longer delay on mobile to prevent premature closing
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const delay = isMobile ? 200 : 100;

      const timeoutId = setTimeout(() => {
        // Listen for both mouse and touch events on mobile
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside, { passive: true });
      }, delay);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [isExpanded]);

  const flushBellNoteSave = async () => {
    if (noteSaveTimerRef.current) {
      window.clearTimeout(noteSaveTimerRef.current);
      noteSaveTimerRef.current = null;
    }
    const html = noteHtmlRef.current || '';
    const plain = stripNoteHtml(html);
    if (!plain) return;
    try {
      if (!noteIdRef.current) {
        if (noteCreateLockRef.current) {
          await noteCreateLockRef.current;
        } else {
          noteCreateLockRef.current = (async () => {
            const title = await nextBellNoteTitle();
            const created = await createNote({
              title,
              contentHtml: html,
              attachments: [],
              sensitiveBlocks: [],
            });
            noteIdRef.current = created?.id || null;
          })();
          await noteCreateLockRef.current;
          noteCreateLockRef.current = null;
        }
        if (noteIdRef.current) {
          await updateNote(noteIdRef.current, { contentHtml: html });
        }
      } else {
        await updateNote(noteIdRef.current, { contentHtml: html });
      }
      window.dispatchEvent(new CustomEvent('workloop:notes-changed'));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data?.error || error?.message || 'Failed to save note',
      }));
    }
  };

  const scheduleBellNoteSave = (html) => {
    const plain = stripNoteHtml(html);
    if (!plain) {
      return;
    }
    noteHtmlRef.current = html;
    setNoteDraftHtml(html);
    if (noteSaveTimerRef.current) {
      window.clearTimeout(noteSaveTimerRef.current);
    }
    noteSaveTimerRef.current = window.setTimeout(() => {
      flushBellNoteSave();
    }, 700);
  };

  const minimizeNoteComposer = (event) => {
    event?.stopPropagation?.();
    flushBellNoteSave();
    setNoteComposerOpen(false);
  };

  const toggleNoteComposer = (event) => {
    event?.stopPropagation?.();
    if (noteComposerOpen) {
      minimizeNoteComposer(event);
      return;
    }
    setNoteComposerOpen(true);
  };

  const startNewBellNote = async (event) => {
    event?.stopPropagation?.();
    await flushBellNoteSave();
    noteHtmlRef.current = '';
    noteIdRef.current = null;
    noteCreateLockRef.current = null;
    setNoteDraftHtml('');
    try {
      const title = await nextBellNoteTitle();
      const created = await createNote({
        title,
        contentHtml: '',
        attachments: [],
        sensitiveBlocks: [],
      });
      noteIdRef.current = created?.id || null;
      window.dispatchEvent(new CustomEvent('workloop:notes-changed'));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data?.error || error?.message || 'Failed to create note',
      }));
    }
    setNoteComposerKey((key) => key + 1);
    setNoteComposerOpen(true);
  };

  useEffect(() => () => {
    if (noteSaveTimerRef.current) {
      window.clearTimeout(noteSaveTimerRef.current);
    }
    const html = noteHtmlRef.current || '';
    if (stripNoteHtml(html) || noteIdRef.current) {
      flushBellNoteSave();
    }
  }, []);

  // Calculate panel position based on bubble location
  const getPanelPosition = () => {
    const panelWidth = noteComposerOpen ? 340 : 250; // md:w-[320px]
    const panelHeight = noteComposerOpen ? 520 : 400; // sm:h-[500px]
    const spacing = 8; // mt-2 = 8px

    // Calculate available space on each side
    const spaceRight = window.innerWidth - position.x - BUBBLE_SIZE;
    const spaceLeft = position.x;
    const spaceBottom = window.innerHeight - position.y - BUBBLE_SIZE;
    const spaceTop = position.y;

    // Determine horizontal position: open on left if not enough space on right
    const openOnLeft = spaceRight < panelWidth && spaceLeft >= panelWidth;

    // Determine vertical position: open on top if not enough space on bottom
    const openOnTop = spaceBottom < panelHeight && spaceTop >= panelHeight;

    // Build style object
    const style = {};

    // Horizontal positioning
    if (openOnLeft) {
      style.right = '0';
      style.left = 'auto';
      style.marginRight = `${spacing}px`;
      style.marginLeft = 'auto';
    } else {
      style.left = '0';
      style.right = 'auto';
      style.marginLeft = `${spacing}px`;
      style.marginRight = 'auto';
    }

    // Vertical positioning
    if (openOnTop) {
      style.bottom = '100%';
      style.top = 'auto';
      style.marginBottom = `${spacing}px`;
      style.marginTop = 'auto';
    } else {
      style.top = '100%';
      style.bottom = 'auto';
      style.marginTop = `${spacing}px`;
      style.marginBottom = 'auto';
    }

    return style;
  };

  // Always show the bell when logged in so audio + highlight controls stay reachable.
  if (!user) {
    return null;
  }

  return (
    <div
      ref={bubbleRef}
      className={`fixed z-[80] transition-transform duration-200 ${isDragging ? 'cursor-grabbing scale-105' : 'cursor-grab'
        }`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        touchAction: 'none'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Main notification bubble */}
      <div
        className="relative bg-primary-600 dark:bg-primary-700 rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200"
        onClick={(e) => {
          e.stopPropagation();
          if (!dragStartedRef.current) {
            setIsExpanded(prev => !prev);
          }
        }}

        onMouseEnter={() => !isDragging && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: '50px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Bell icon */}
        <svg
          className="w-7 h-7 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Unread message count badge */}
        {totalBadgeCount > 0 && (
          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 ring-2 ring-white dark:ring-gray-900">
            {totalBadgeCount > 99 ? '99+' : totalBadgeCount}
          </div>
        )}
        {hasPanelNotifications && totalUnreadCount === 0 && !hasMeetingNotifications && (
          <div
            className="absolute -bottom-0.5 -left-0.5 bg-blue-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center ring-2 ring-white dark:ring-gray-900"
            title="Shared panel updates"
          >
            P
          </div>
        )}
        {hasMeetingNotifications && totalUnreadCount === 0 && (
          <div
            className="absolute -bottom-0.5 -left-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center ring-2 ring-white dark:ring-gray-900"
            title="Meeting updates"
          >
            M
          </div>
        )}
        {mentionConversationCount > 0 && (
          <div
            className="absolute -bottom-0.5 -left-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center ring-2 ring-white dark:ring-gray-900"
            title={`${mentionConversationCount} mention${mentionConversationCount !== 1 ? 's' : ''}`}
          >
            @
          </div>
        )}
      </div>

      {/* Expanded unread messages panel */}
      {isExpanded && (
        <div
          className={`absolute bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col ${
            noteComposerOpen
              ? 'w-[22rem] sm:w-[24rem] h-[32rem]'
              : 'w-80 sm:w-96 md:w-[320px] h-[400px] sm:h-[500px]'
          }`}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          style={getPanelPosition()}
        >
          {/* Header - Draggable */}
          <div
            className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex-shrink-0 cursor-move select-none"
            onMouseDown={handleHeaderMouseDown}
            onTouchStart={handleHeaderTouchStart}
            style={{ touchAction: 'none' }}
          >
            <div>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Notifications ({totalBadgeCount})
                </h3>
                {hasMeetingNotifications && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                    {fabMeetingNotifications.length} meeting update{fabMeetingNotifications.length !== 1 ? 's' : ''}
                  </p>
                )}
                {hasPanelNotifications && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                    {fabPanelNotifications.length} panel access granted
                  </p>
                )}
                {hasTaskNotifications && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
                    {fabTaskNotifications.length} task{fabTaskNotifications.length !== 1 ? 's' : ''} delegated to you
                  </p>
                )}
                {hasUserNotifications && (
                  <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">
                    {fabUserNotifications.length} assignment{fabUserNotifications.length !== 1 ? 's' : ''} or status update{fabUserNotifications.length !== 1 ? 's' : ''}
                  </p>
                )}
                {mentionConversationCount > 0 && (
                  <p className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
                    {mentionConversationCount} mention{mentionConversationCount !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  title="Take note"
                  aria-label="Take note"
                  onClick={toggleNoteComposer}
                  className={`p-1.5 rounded-md cursor-pointer ${
                    noteComposerOpen
                      ? 'text-primary-700 bg-primary-100 dark:text-primary-200 dark:bg-primary-900/50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800'
                  }`}
                  style={{ touchAction: 'auto' }}
                >
                  <TakeNoteGlyph className="w-5 h-5" />
                </button>
                <WorkModeSpeechControls />
                <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(false);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  setIsExpanded(false);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
                style={{ touchAction: 'auto' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <HighlightNotificationToggle />
              <EnableAudioCaptionToggle />
            </div>
            </div>
          </div>

          <div className={`flex-1 min-h-0 flex-col p-3 gap-2 ${noteComposerOpen ? 'flex' : 'hidden'}`}>
              <div className="flex-1 min-h-0">
                <CompactRichEditor
                  key={noteComposerKey}
                  quillRef={noteQuillRef}
                  value={noteDraftHtml}
                  uncontrolled
                  onChange={scheduleBellNoteSave}
                  placeholder="Write a note…"
                  minHeight={180}
                  maxHeight={280}
                  toolbarMode="bell"
                  enableMentions={false}
                  instanceClass="bell-take-note-editor"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={startNewBellNote}
                  className="px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                >
                  Create new
                </button>
                <button
                  type="button"
                  onClick={minimizeNoteComposer}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>

          {/* Content - Scrollable */}
          <div className={`flex-1 overflow-y-auto ${noteComposerOpen ? 'hidden' : ''}`}>
            {fabMeetingNotifications.length > 0 && (
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2 px-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Meetings
                  </span>
                  <button
                    type="button"
                    onClick={handleClearAllMeetingNotifications}
                    className="text-[10px] text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                  >
                    Clear all
                  </button>
                </div>

                {fabMeetingNotifications.map((event) => (
                  <div
                    key={event.fabKey}
                    className="flex items-start gap-2 p-2 mb-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 group"
                  >
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => handleMeetingNotificationClick(event)}
                        className="w-full text-left"
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                            String(event.type).toUpperCase() === 'MEETING_REMINDER'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                              : String(event.type).toUpperCase() === 'MEETING_CANCELLED'
                                ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
                                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                          }`}>
                            {meetingTypeLabel(event.type)}
                          </span>
                        </div>
                        <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                          {event.title || 'Meeting'}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                          {event.notificationBody
                            || [event.date, event.startTime].filter(Boolean).join(' · ')}
                        </p>
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleDismissMeetingNotification(e, event.fabKey)}
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 opacity-70 group-hover:opacity-100"
                      title="Dismiss"
                      aria-label="Dismiss notification"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {fabPanelNotifications.length > 0 && (
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2 px-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Panel access granted
                  </span>
                  <button
                    type="button"
                    onClick={handleClearAllPanelNotifications}
                    className="text-[10px] text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                  >
                    Clear all
                  </button>
                </div>

                {fabPanelNotifications.map((event) => {
                  const formatted = formatPanelAccessNotification(event);
                  return (
                    <div
                      key={event.fabKey}
                      className="flex items-start gap-2 p-2 mb-1 rounded-lg bg-gray-50 dark:bg-gray-700/50 group"
                    >
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={handleOpenDailyTasks}
                          className="w-full text-left"
                        >
                          <p className="text-xs text-gray-700 dark:text-gray-300 truncate">
                            {formatted.body}
                          </p>
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleDismissPanelNotification(e, event.fabKey)}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 opacity-70 group-hover:opacity-100"
                        title="Dismiss"
                        aria-label="Dismiss notification"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {fabTaskNotifications.length > 0 && (
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2 px-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Tasks delegated to you
                  </span>
                  <button
                    type="button"
                    onClick={handleClearAllTaskNotifications}
                    className="text-[10px] text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                  >
                    Clear all
                  </button>
                </div>

                {fabTaskNotifications.map((event) => {
                  const formatted = formatTaskDelegatedNotification(event);
                  return (
                    <div
                      key={event.fabKey}
                      className="flex items-start gap-2 p-2 mb-1 rounded-lg bg-gray-50 dark:bg-gray-700/50 group"
                    >
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => handleOpenTaskNotification(event)}
                          className="w-full text-left"
                        >
                          <p className="text-xs text-gray-700 dark:text-gray-300 truncate">
                            {formatted.body}
                          </p>
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleDismissTaskNotification(e, event.fabKey)}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 opacity-70 group-hover:opacity-100"
                        title="Dismiss"
                        aria-label="Dismiss notification"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {fabUserNotifications.length > 0 && (
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2 px-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Assigned to you
                  </span>
                  <button
                    type="button"
                    onClick={handleClearAllUserNotifications}
                    className="text-[10px] text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                  >
                    Clear all
                  </button>
                </div>

                {fabUserNotifications.map((event) => {
                  const formatted = formatUserNotification(event);
                  return (
                    <div
                      key={event.fabKey}
                      className="flex items-start gap-2 p-2 mb-1 rounded-lg bg-gray-50 dark:bg-gray-700/50 group"
                    >
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => handleOpenUserNotification(event)}
                          className="w-full text-left"
                        >
                          <p className="text-xs text-gray-700 dark:text-gray-300 truncate">
                            {formatted.body}
                          </p>
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => handleDismissUserNotification(e, event.fabKey)}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 opacity-70 group-hover:opacity-100"
                        title="Dismiss"
                        aria-label="Dismiss notification"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {totalUnreadCount === 0 ? (
              !hasPanelNotifications && !hasMeetingNotifications && !hasTaskNotifications && !hasUserNotifications ? (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <svg
                  className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
                <p className="text-gray-500 dark:text-gray-400 text-sm">No unread messages</p>
              </div>
              ) : null
            ) : (
              <div className="p-2">
                {mentionConversationCount > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-2 px-2">
                      Mentions ({mentionConversationCount})
                    </h4>
                    {channelsWithUnreadMentionsAll.map((channel) => {
                      const unreadCount = Math.max(0, parseInt(unreadCounts[channel.id]) || 0);
                      const mentionPreview = getMentionPreviewForChannel(
                        channel.id,
                        channel.name || 'Channel',
                        false
                      );
                      if (!mentionPreview) return null;
                      return (
                        <div
                          key={`mention-${channel.id}`}
                          className="mb-1 rounded-lg border-l-4 border-primary-500 bg-primary-50 dark:bg-primary-900/40 overflow-hidden"
                        >
                        <div
                          onClick={(e) => handleChannelClick(channel, e, false, { preferMention: true })}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleChannelClick(channel, e, true, { preferMention: true });
                          }}
                          className="flex items-center justify-between p-3 hover:bg-primary-100 dark:hover:bg-primary-900/60 cursor-pointer transition-colors active:bg-primary-200 dark:active:bg-primary-900/80"
                        >
                          <div className="flex items-center flex-1 min-w-0">
                            <ChannelRowAvatar channel={channel} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={mentionPreview.title}>
                                {mentionPreview.title}
                              </p>
                              {mentionPreview.body && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5" title={mentionPreview.body}>
                                  {mentionPreview.body}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-3">
                            <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-semibold text-white bg-red-500 rounded-full">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          </div>
                        </div>
                        </div>
                      );
                    })}
                    {dmsWithUnreadMentionsAll.map((dm) => {
                      const unreadCount = Math.max(0, parseInt(unreadCounts[dm.id]) || 0);
                      const otherUser = getOtherUserFromDM(dm);
                      const dmLabel = getDmDisplayName(otherUser);
                      const mentionPreview = getMentionPreviewForChannel(dm.id, dmLabel, true, otherUser);
                      if (!mentionPreview) return null;
                      return (
                        <div
                          key={`mention-dm-${dm.id}`}
                          className="mb-1 rounded-lg border-l-4 border-primary-500 bg-primary-50 dark:bg-primary-900/40 overflow-hidden"
                        >
                        <div
                          onClick={(e) => handleChannelClick(dm, e, false, { preferMention: true })}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleChannelClick(dm, e, true, { preferMention: true });
                          }}
                          className="flex items-center justify-between p-3 hover:bg-primary-100 dark:hover:bg-primary-900/60 cursor-pointer transition-colors active:bg-primary-200 dark:active:bg-primary-900/80"
                        >
                          <div className="flex items-center flex-1 min-w-0">
                            <DmRowAvatar otherUser={otherUser} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {mentionPreview.title}
                              </p>
                              {mentionPreview.body && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                  {mentionPreview.body}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-3">
                            <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-semibold text-white bg-red-500 rounded-full">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          </div>
                        </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Channels with unread messages */}
                {channelsWithUnreadOnly.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2">
                      Channels
                    </h4>
                    {channelsWithUnreadOnly.map((channel) => {
                      const unreadCount = Math.max(0, parseInt(unreadCounts[channel.id]) || 0);
                      return (
                        <div
                          key={channel.id}
                          className="mb-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 overflow-hidden"
                        >
                        <div
                          onClick={(e) => handleChannelClick(channel, e, false)}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleChannelClick(channel, e, true);
                          }}
                          className="flex items-center justify-between p-3 cursor-pointer transition-colors group active:bg-gray-200 dark:active:bg-gray-600"
                        >
                          <div className="flex items-center flex-1 min-w-0">
                            <ChannelRowAvatar channel={channel} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {channel.name || 'Unnamed Channel'}
                              </p>
                              {(() => {
                                const preview = getUnreadRowPreview(
                                  channel.id,
                                  channel.name || 'Unnamed Channel',
                                  false
                                );
                                return preview?.body ? (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5" title={preview.body}>
                                    {preview.body}
                                  </p>
                                ) : null;
                              })()}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-3">
                            <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-semibold text-white bg-red-500 rounded-full">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          </div>
                        </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Direct Messages with unread messages */}
                {dmsWithUnreadOnly.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2">
                      Direct Messages
                    </h4>
                    {dmsWithUnreadOnly.map((dm) => {
                      const unreadCount = Math.max(0, parseInt(unreadCounts[dm.id]) || 0);
                      const otherUser = getOtherUserFromDM(dm);
                      const dmLabel = getDmDisplayName(otherUser);
                      console.log('dmLabel', dmLabel);
                      console.log('otherUser', otherUser);
                      console.log('dm', dm);
                      return (
                        <div
                          key={dm.id}
                          className="mb-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 overflow-hidden"
                        >
                        <div
                          onClick={(e) => handleChannelClick(dm, e, false)}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleChannelClick(dm, e, true);
                          }}
                          className="flex items-center justify-between p-3 cursor-pointer transition-colors group active:bg-gray-200 dark:active:bg-gray-600"
                        >
                          <div className="flex items-center flex-1 min-w-0">
                            <DmRowAvatar otherUser={otherUser} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {otherUser ? otherUser.firstName + ' ' + otherUser.lastName : 'Unknown User'}
                              </p>
                              {(() => {
                                const preview = getUnreadRowPreview(
                                  dm.id,
                                  getDmDisplayName(otherUser),
                                  true,
                                  otherUser
                                );
                                return preview?.body ? (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5" title={preview.body}>
                                    {preview.body}
                                  </p>
                                ) : null;
                              })()}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-3">
                            <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-semibold text-white bg-red-500 rounded-full">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          </div>
                        </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tooltip on hover when not expanded */}
      {!isExpanded && totalUnreadCount > 0 && isHovered && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 pointer-events-none transition-opacity duration-200 z-10">
          <div className="font-semibold mb-1">Unread Messages</div>
          <div className="text-gray-300">
            {totalUnreadCount} unread message{totalUnreadCount !== 1 ? 's' : ''} across {channelsWithUnread.length + dmsWithUnread.length} conversation{channelsWithUnread.length + dmsWithUnread.length !== 1 ? 's' : ''}
            {mentionConversationCount > 0 && (
              <> · {mentionConversationCount} mention{mentionConversationCount !== 1 ? 's' : ''}</>
            )}
          </div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
            <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraggableNotificationBubble;
